import { Dispatch, SetStateAction, useState } from 'react'
import {
    getMultiFactorResolver,
    MultiFactorResolver,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    signInWithEmailAndPassword,
} from 'firebase/auth'

import { auth } from '~config/firebase'
import { useRecaptcha } from '~utils/hooks/use-recaptcha'
import {
    AuthErrorInfo,
    SUPPORT_EMAIL,
    getLoginErrorInfo,
} from '~utils/auth-errors'
import type { LoginStatus } from '~config/user-context'

/**
 * State describing an in-progress phone multi-factor challenge. When `isOpen`
 * is true, an `MFAModal` should be shown so the user can enter the SMS code.
 */
export interface MfaChallenge {
    isOpen: boolean
    verificationId: string | undefined
    resolver: MultiFactorResolver | undefined
    /** Abandon the current MFA challenge and reset the reCAPTCHA verifier. */
    cancel: () => void
}

export interface EmailPasswordAuth {
    authError: AuthErrorInfo | undefined
    setAuthError: Dispatch<SetStateAction<AuthErrorInfo | undefined>>
    /**
     * Attempt an email + password sign-in. Returns the resulting
     * {@link LoginStatus}:
     * - `'success'` – Firebase signed the user in. The caller is responsible
     *   for any post-login navigation.
     * - `'requires-mfa'` – a phone MFA challenge was started; `mfa.isOpen` is
     *   now true and an `MFAModal` should be rendered.
     * - `'error'` – sign-in failed; `authError` describes what to show.
     */
    signIn: (email: string, password: string) => Promise<LoginStatus>
    mfa: MfaChallenge
}

/**
 * Encapsulates the Firebase email/password sign-in flow, including the phone
 * multi-factor (MFA) challenge and its invisible reCAPTCHA. Shared by the main
 * login page and the inactivity lock screen so there is a single source of
 * truth for authentication behavior.
 *
 * @param recaptchaContainerId id of a DOM element the caller renders for the
 * invisible reCAPTCHA widget. Each mounted consumer must pass a unique id.
 */
export function useEmailPasswordAuth(
    recaptchaContainerId: string,
): EmailPasswordAuth {
    const { recaptchaVerifier, resetVerifier } =
        useRecaptcha(recaptchaContainerId)

    const [authError, setAuthError] = useState<AuthErrorInfo | undefined>()
    const [resolver, setResolver] = useState<MultiFactorResolver>()
    const [verificationId, setVerificationId] = useState<string>()

    function startMfaChallenge(mfaResolver: MultiFactorResolver) {
        if (!recaptchaVerifier) {
            setAuthError(getLoginErrorInfo(undefined))
            resetVerifier()
            return
        }

        setResolver(mfaResolver)

        const phoneHintIndex = mfaResolver.hints.findIndex(
            hint => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
        )
        const phoneInfoOptions = {
            multiFactorHint: mfaResolver.hints[phoneHintIndex],
            session: mfaResolver.session,
        }
        const phoneAuthProvider = new PhoneAuthProvider(auth)
        phoneAuthProvider
            .verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
            .then(id => setVerificationId(id))
            .catch(err => {
                console.error('Failed to send MFA code', err)
                setAuthError({
                    title: "We couldn't send your verification code",
                    description:
                        'Please try again. If the problem keeps happening, our support team can help.',
                    primaryAction: {
                        label: `Email ${SUPPORT_EMAIL}`,
                        mailto: SUPPORT_EMAIL,
                    },
                })
            })
    }

    async function signIn(
        email: string,
        password: string,
    ): Promise<LoginStatus> {
        try {
            const userCredential = await signInWithEmailAndPassword(
                auth,
                email,
                password,
            )
            return userCredential.user ? 'success' : 'error'
        } catch (error) {
            const code = (error as { code?: string }).code
            if (code === 'auth/multi-factor-auth-required') {
                startMfaChallenge(getMultiFactorResolver(auth, error as Error))
                return 'requires-mfa'
            }
            console.error('Failed to sign in', error)
            setAuthError(getLoginErrorInfo(code))
            return 'error'
        }
    }

    function cancelMfa() {
        setVerificationId(undefined)
        setResolver(undefined)
        resetVerifier()
    }

    return {
        authError,
        setAuthError,
        signIn,
        mfa: {
            isOpen: !!verificationId && !!resolver,
            verificationId,
            resolver,
            cancel: cancelMfa,
        },
    }
}
